import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { inputCls } from './shared';

const errMsg = (e) => e?.response?.data?.detail || 'Erreur';

const SplitInput = ({ value, onChange, testid }) => (
  <label className="block text-xs font-semibold text-gray-600">
    Part de commission pour l'agence qui recommande (%)
    <input type="number" min="0" max="100" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
  </label>
);

const validSplit = (v) => v !== '' && Number(v) >= 0 && Number(v) <= 100;

export const InviteDialog = ({ open, onClose, directory, onDone, presetAgencyId = '' }) => {
  const [agencyId, setAgencyId] = useState(presetAgencyId);
  const [split, setSplit] = useState(50);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setAgencyId(presetAgencyId); }, [open, presetAgencyId]);
  const available = directory.filter((a) => !a.partnership_status);

  const submit = async () => {
    if (!agencyId) { toast.error('Choisissez une agence'); return; }
    if (!validSplit(split)) { toast.error('Pourcentage invalide (0 à 100)'); return; }
    setBusy(true);
    try {
      await api.post('/partnerships', { partner_agency_id: agencyId, commission_split: Number(split), message: message || null });
      toast.success('Invitation envoyée'); setMessage(''); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="partnership-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Inviter une agence</DialogTitle>
          <DialogDescription>Le partenariat démarre quand l'administrateur de l'autre agence accepte.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-gray-600">Agence
            <select value={agencyId} onChange={(e) => setAgencyId(e.target.value)} className={inputCls} data-testid="partnership-invite-agency-select">
              <option value="">— Choisir —</option>
              {available.map((a) => <option key={a.id} value={a.id}>{a.name}{a.city ? ` (${a.city})` : ''}</option>)}
            </select>
          </label>
          <SplitInput value={split} onChange={setSplit} testid="partnership-invite-split-input" />
          <label className="block text-xs font-semibold text-gray-600">Message (optionnel)
            <textarea rows={2} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} className={inputCls} data-testid="partnership-invite-message-input" />
          </label>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="partnership-invite-submit">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Envoyer l'invitation
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const SuperLinkDialog = ({ open, onClose, onDone }) => {
  const [agencies, setAgencies] = useState([]);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [split, setSplit] = useState(50);
  useEffect(() => {
    if (open) api.get('/access/agencies').then((r) => setAgencies(r.data || [])).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!a || !b || a === b) { toast.error('Choisissez deux agences différentes'); return; }
    if (!validSplit(split)) { toast.error('Pourcentage invalide (0 à 100)'); return; }
    try {
      await api.post('/partnerships/link', { agency_a: a, agency_b: b, commission_split: Number(split) });
      toast.success('Agences reliées'); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Pick = ({ value, onChange, label, testid }) => (
    <label className="block text-xs font-semibold text-gray-600">{label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
        <option value="">— Choisir —</option>
        {agencies.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="partnership-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Relier deux agences</DialogTitle>
          <DialogDescription>Le partenariat est actif immédiatement, sans invitation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Pick value={a} onChange={setA} label="Première agence" testid="partnership-link-agency-a" />
          <Pick value={b} onChange={setB} label="Seconde agence" testid="partnership-link-agency-b" />
          <SplitInput value={split} onChange={setSplit} testid="partnership-link-split-input" />
        </div>
        <Button onClick={submit} className="w-full bg-[#2B3A2C] hover:bg-[#1f2b20] text-white rounded-xl" data-testid="partnership-link-submit">Relier</Button>
      </DialogContent>
    </Dialog>
  );
};
